import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import api, { mediaUrl } from "@/lib/api";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/projects", label: "Our Projects" },
  { to: "/team", label: "Our Team" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [site, setSite] = useState({});
  const loc = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    api.get("/site/content").then((r) => setSite(r.data || {})).catch(() => {});
  }, []);

  useEffect(() => {
    if (site.favicon_url) {
      let link = document.querySelector("link[rel~='icon']");
      if (!link) {
        link = document.createElement("link");
        link.rel = "icon";
        document.head.appendChild(link);
      }
      link.href = mediaUrl(site.favicon_url);
    }
  }, [site.favicon_url]);

  useEffect(() => {
    setOpen(false);
  }, [loc.pathname]);

  const onDark = loc.pathname === "/" && !scrolled;

  return (
    <header
      data-testid="public-navbar"
      className={`fixed top-0 left-0 right-0 z-40 transition-colors duration-300 ${
        scrolled ? "bg-white/85 backdrop-blur-xl border-b border-line" : onDark ? "bg-transparent" : "bg-white/85 backdrop-blur-xl border-b border-line"
      }`}
    >
      <div
        className="max-w-[1600px] mx-auto px-6 lg:px-12 flex items-center justify-between transition-[padding] duration-300"
        style={{ paddingTop: site.logo_url ? 10 : 12, paddingBottom: site.logo_url ? 10 : 12 }}
      >
        <Link to="/" data-testid="nav-logo" className={`inline-flex items-center gap-3 font-heading text-lg tracking-tight ${onDark ? "text-white" : "text-ink"}`}>
          {(() => {
            // Choose which logo to show. When we're on the dark hero we
            // prefer the primary logo_url. On light backgrounds we prefer
            // logo_url_light (rendered as-is); if not set, we fall back to
            // logo_url with a brightness-0 filter to force a black silhouette.
            const primary = site.logo_url;
            const light = site.logo_url_light;
            const activeSrc = onDark ? (primary || light) : (light || primary);
            const applyBlack = !onDark && !light && !!primary;
            if (!activeSrc) {
              return (
                <>
                  <span className="font-light">AS</span>{" "}
                  <span className="font-medium">DESIGN STUDIOS</span>
                </>
              );
            }
            return (
              <img
                src={mediaUrl(activeSrc)}
                alt="AS Design Studios"
                style={{ height: `${Math.max(24, Math.min(160, site.logo_size || 64))}px` }}
                className={`w-auto object-contain ${applyBlack ? "brightness-0" : ""}`}
              />
            );
          })()}
        </Link>

        <nav className="hidden md:flex items-center gap-10">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `nav-link text-sm tracking-wide ${
                  onDark ? "text-white/90 hover:text-white" : "text-ink/80 hover:text-ink"
                } ${isActive ? "font-medium" : "font-normal"}`
              }
              end={n.to === "/"}
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen((v) => !v)}
          className={`md:hidden ${onDark ? "text-white" : "text-ink"}`}
          aria-label="Menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div className="md:hidden bg-white border-t border-line" data-testid="nav-mobile-panel">
          <div className="px-6 py-6 flex flex-col gap-5">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`nav-mobile-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="font-heading text-2xl text-ink"
                end={n.to === "/"}
              >
                {n.label}
              </NavLink>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}
